'use client';

import { BookCard, EmptyState, Input, SectionHeading, Spinner } from '@lectura/ui';
import { BookOpen, Search } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { LoginRequired } from '@/components/LoginRequired';
import { SiteShell } from '@/components/SiteShell';
import { useAuth } from '@/lib/auth';
import { useLibrary } from '@/lib/queries';
import { useHydrated } from '@/lib/use-hydrated';

export default function LibraryPage() {
  const hydrated = useHydrated();
  const isLogged = useAuth((s) => s.isAuthenticated());
  const [search, setSearch] = useState('');
  const { data, isLoading, isError } = useLibrary({ search });

  if (!hydrated || !isLogged) {
    return (
      <LoginRequired
        title="A könyvtár csak bejelentkezve böngészhető"
        description="A katalógus a tenanted publikált kiadványait tartalmazza — lépj be, hogy lásd."
      />
    );
  }

  return (
    <SiteShell>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Könyvtár"
          title="Minden publikált kiadvány"
          description="Keresd meg, ami érdekel, vagy szűrj cím szerint."
        />

        <div className="mb-8 max-w-md">
          <Input
            type="search"
            placeholder="Keresés cím, szerző, kulcsszó alapján…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftSlot={<Search className="size-4" />}
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Spinner className="size-8" />
          </div>
        ) : isError ? (
          <EmptyState
            icon={<BookOpen />}
            title="Hiba a betöltés közben"
            description="A könyvtár átmenetileg nem érhető el. Próbáld újra később."
          />
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            icon={<BookOpen />}
            title="Nincs találat"
            description="Próbálj egyszerűbb keresést, vagy lépj a kezdőlapra."
          />
        ) : (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {data.data.map((pub) => (
              <Link key={pub.id} href={`/read/${pub.id}`} aria-label={pub.title}>
                <BookCard
                  title={pub.title}
                  authors={pub.contributors
                    .filter((c) => c.role === 'author')
                    .map((c) => c.name)}
                  coverUrl={pub.coverUrl ?? undefined}
                />
              </Link>
            ))}
          </div>
        )}
      </div>
    </SiteShell>
  );
}
